import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as StudentIcon,
  PersonAdd as TeacherIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface AttendanceData {
  period: {
    start_date: string;
    end_date: string;
    days_analyzed: number;
  };
  student_attendance: {
    total_records: number;
    overall_rate: number;
    status_breakdown: {
      present: number;
      absent: number;
      late: number;
      excused: number;
    };
    by_gender: any[];
    by_class: any[];
    chronic_absenteeism: {
      total_chronic_absentees: number;
      percentage_of_students: number;
    };
  };
  teacher_attendance: {
    total_records: number;
    overall_rate: number;
    status_breakdown: {
      present: number;
      absent: number;
      on_leave: number;
    };
    learning_impact: {
      classes_missed: number;
      students_affected: number;
      days_with_disruption: number;
    };
    by_position: any[];
  };
  school_analysis: {
    total_schools_analyzed: number;
    performance_categories: {
      excellent: number;
      good: number;
      needs_improvement: number;
    };
    top_performing: any[];
    bottom_performing: any[];
  };
  trends: {
    daily: any[];
    weekly: any[];
    trend_analysis: {
      trend: string;
      description: string;
    };
  };
  alerts: any[];
}

const AttendanceOverview: React.FC = () => {
  const [attendanceData, setAttendanceData] = useState<AttendanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAttendanceData();
  }, []);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Import the attendance API service
      const { attendanceAPI } = await import('../../services/attendanceApi');

      const response = await attendanceAPI.getOverview();

      if (response.data.status === 'success') {
        setAttendanceData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load attendance data');
      }
    } catch (err: any) {
      console.error('Attendance overview error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'error';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend.toLowerCase()) {
      case 'improving':
        return <TrendingUpIcon color="success" />;
      case 'declining':
        return <WarningIcon color="error" />;
      default:
        return <TrendingUpIcon color="action" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!attendanceData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No attendance data available
      </Alert>
    );
  }

  // Prepare chart data
  const dailyTrends = attendanceData.trends?.daily || [];
  const chartData = {
    labels: dailyTrends.slice(-7).map((item: any) =>
      new Date(item.summary_date).toLocaleDateString()
    ),
    datasets: [
      {
        label: 'Student Attendance %',
        data: dailyTrends.slice(-7).map((item: any) => item.avg_student_rate),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
      {
        label: 'Teacher Attendance %',
        data: dailyTrends.slice(-7).map((item: any) => item.avg_teacher_rate),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Attendance Trends (Last 7 Days)',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Attendance Overview
      </Typography>

      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Period: {attendanceData.period.start_date} to {attendanceData.period.end_date}
        ({attendanceData.period.days_analyzed} days)
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <StudentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Student Attendance</Typography>
              </Box>
              <Typography variant="h3" color={getAttendanceColor(attendanceData.student_attendance.overall_rate)}>
                {attendanceData.student_attendance.overall_rate.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={attendanceData.student_attendance.overall_rate}
                color={getAttendanceColor(attendanceData.student_attendance.overall_rate)}
                sx={{ mt: 1 }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {attendanceData.student_attendance.total_records} total records
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <TeacherIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Teacher Attendance</Typography>
              </Box>
              <Typography variant="h3" color={getAttendanceColor(attendanceData.teacher_attendance.overall_rate)}>
                {attendanceData.teacher_attendance.overall_rate.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={attendanceData.teacher_attendance.overall_rate}
                color={getAttendanceColor(attendanceData.teacher_attendance.overall_rate)}
                sx={{ mt: 1 }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {attendanceData.teacher_attendance.total_records} total records
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SchoolIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Schools Analyzed</Typography>
              </Box>
              <Typography variant="h3" color="primary">
                {attendanceData.school_analysis.total_schools_analyzed}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`${attendanceData.school_analysis.performance_categories.excellent} Excellent`}
                  color="success"
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
                <Chip
                  label={`${attendanceData.school_analysis.performance_categories.needs_improvement} Need Help`}
                  color="error"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                {getTrendIcon(attendanceData.trends?.trend_analysis?.trend || 'stable')}
                <Typography variant="h6" sx={{ ml: 1 }}>Trend</Typography>
              </Box>
              <Typography variant="h4" sx={{ textTransform: 'capitalize' }}>
                {attendanceData.trends?.trend_analysis?.trend || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {attendanceData.trends?.trend_analysis?.description || 'No trend data available'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Line data={chartData} options={chartOptions} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Learning Impact
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Classes Missed</Typography>
                <Typography variant="h4" color="error">
                  {attendanceData.teacher_attendance.learning_impact?.classes_missed || 0}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Students Affected</Typography>
                <Typography variant="h4" color="warning.main">
                  {attendanceData.teacher_attendance.learning_impact?.students_affected || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Days with Disruption</Typography>
                <Typography variant="h4" color="info.main">
                  {attendanceData.teacher_attendance.learning_impact?.days_with_disruption || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {(attendanceData.alerts?.length || 0) > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Alerts ({attendanceData.alerts?.length || 0})
            </Typography>
            {(attendanceData.alerts || []).map((alert: any, index: number) => (
              <Alert
                key={index}
                severity={alert.type === 'critical' ? 'error' : 'warning'}
                sx={{ mb: 1 }}
              >
                <strong>{alert.category}:</strong> {alert.message}
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top Performing Schools */}
      {(attendanceData.school_analysis?.top_performing?.length || 0) > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Performing Schools
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>School Name</TableCell>
                    <TableCell>District</TableCell>
                    <TableCell align="right">Student Rate</TableCell>
                    <TableCell align="right">Teacher Rate</TableCell>
                    <TableCell align="right">Days Reported</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceData.school_analysis.top_performing.slice(0, 5).map((school: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{school.school__name}</TableCell>
                      <TableCell>{school.school__district__name || 'N/A'}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${school.avg_student_rate?.toFixed(1) || 0}%`}
                          color={getAttendanceColor(school.avg_student_rate || 0)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${school.avg_teacher_rate?.toFixed(1) || 0}%`}
                          color={getAttendanceColor(school.avg_teacher_rate || 0)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{school.days_reported}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AttendanceOverview;